/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { useMemo } from "react";

import { BasePlayerCallbacks, BasePlayerConfig, PlayerTheme } from "../types";

import { PlayerCallbacksProvider } from "./PlayerCallbacksContext";
import { PlayerConfigProvider } from "./PlayerConfigContext";
import { PlayerTranslationProvider } from "./PlayerTranslationContext";

import "../styles/theme.scss";

interface BasePlayerProviderProps {
  config: BasePlayerConfig;
  callbacks?: BasePlayerCallbacks;
  theme?: PlayerTheme;
  t?: (key: string) => string;
  children: React.ReactNode;
}

const themeVarMap: Record<keyof PlayerTheme, string> = {
  primaryColor: "--player-primary-color",
  bgColor: "--player-bg-color",
  bgGradientColor: "--player-bg-gradient-color",
  bgTertiaryColor: "--player-bg-tertiary-color",
  primaryTextColor: "--player-primary-text-color",
  errorColor: "--player-error-color",
  iconDarkColor: "--player-icon-dark-color",
  progressAdsColor: "--player-progress-ads-color",
  iconWithoutTextSize: "--player-icon-without-text-size",
  fontWeightNormal: "--player-font-weight-normal",
  fontWeightMedium: "--player-font-weight-medium",
  fontWeightBold: "--player-font-weight-bold",
  fontWeightUltraBold: "--player-font-weight-ultra-bold",
};

function buildThemeStyle(theme?: PlayerTheme): React.CSSProperties | undefined {
  if (!theme) return undefined;

  const style: Record<string, string | number> = {};
  for (const [key, cssVar] of Object.entries(themeVarMap)) {
    const value = theme[key as keyof PlayerTheme];
    if (value !== undefined) {
      style[cssVar] = value;
    }
  }

  return Object.keys(style).length > 0
    ? (style as unknown as React.CSSProperties)
    : undefined;
}

export const BasePlayerProvider: React.FC<BasePlayerProviderProps> = ({
  config,
  callbacks = {},
  theme,
  t,
  children,
}) => {
  const themeStyle = useMemo(() => buildThemeStyle(theme), [theme]);

  return (
    <PlayerConfigProvider config={config}>
      <PlayerCallbacksProvider callbacks={callbacks}>
        <PlayerTranslationProvider t={t}>
          <div className="WebPlayer" style={themeStyle}>
            {children}
          </div>
        </PlayerTranslationProvider>
      </PlayerCallbacksProvider>
    </PlayerConfigProvider>
  );
};
